import { Transition } from '@headlessui/react';
import { MenuIcon, UserCircleIcon } from '@heroicons/react/outline';
import { useEffect, useState } from 'react';
import Button from '~/components/Button';
import MainFooter from '~/components/MainFooter';
import Pricing from '~/components/Pricing';
import Sidebar from '~/components/Sidebar';
import logo from '~/images/logo.svg';
import bottleImage from '~/images/long-bottle.jpg';
import { Link, useLocation, type LinksFunction } from '~/remix';
import { useUser } from '~/utils';
import { Testimonials } from './Testimonials';
import routeStyles from './_route.module.css';
import { useLoaderData } from '@remix-run/react';
import { loader } from './_route.server';
export { loader };

export let links: LinksFunction = () => [
  { rel: 'stylesheet', href: routeStyles },
];

export default function Index() {
  const { showProductHunt } = useLoaderData<typeof loader>();
  const [showing, setShowing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const user = useUser();
  const location = useLocation();
  useEffect(() => {
    setTimeout(() => setShowing(true), 1);
  }, []);

  return (
    <div className="inset-0 flex justify-center h-full min-h-screen bg-accent-dark">
      {user && (
        <Sidebar
          open={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          isAdmin={user?.isAdmin}
        />
      )}
      <div className="flex flex-col flex-1 bg-white">
        <header className="flex flex-col">
          <div className="z-10 flex items-center px-4 py-2">
            {user && (
              <button
                type="button"
                className="pr-4 text-gray-500 focus:outline-none"
                onClick={() => setSidebarOpen(true)}
              >
                <span className="sr-only">Open sidebar</span>
                <MenuIcon className="w-6 h-6" aria-hidden="true" />
              </button>
            )}
            <img
              src={logo}
              className="h-[20px] w-[25px] sm:h-[30px] sm:w-[38px]"
              alt="Sunset Messages"
            />
            <h1 className="mt-1 ml-3 text-lg tracking-widest uppercase sm:text-2xl">
              <Link to="/">Sunset Messages</Link>
            </h1>
            <div className="flex justify-end flex-1">
              {user ? (
                <button
                  type="button"
                  className="flex items-center justify-end gap-4"
                  onClick={() => setSidebarOpen(true)}
                >
                  <span className="sr-only">Open sidebar</span>
                  <UserCircleIcon className="w-5 h-5" />
                  <span className="hidden sm:inline-block">{user.name}</span>
                </button>
              ) : location.pathname !== '/login' ? (
                <Link to="/login">Login</Link>
              ) : null}
            </div>
          </div>
          <div className="header-image-container">
            <img src={bottleImage} alt="header-bottle"></img>

            <Transition
              show={showing}
              enter="transition ease-out duration-[3000ms] xl:duration-[4000ms] transform"
              enterFrom="-translate-y-[24px] xl:-translate-y-[48px] opacity-0"
              enterTo="translate-y-0 opacity-100"
            >
              <h1
                className="header-text absolute right-8 top-4 hidden text-right
                text-xl font-bold text-ssm-purple
                sm:right-8 sm:top-8 sm:block sm:text-4xl sm:leading-[0.7]
                md:text-[2.75rem] md:leading-[0.8]
                xl:right-16 xl:top-12 xl:text-[5.5rem] xl:leading-[0.7]
                2xl:right-20 2xl:top-16 2xl:text-[6rem] 2xl:leading-[0.8]"
              >
                Sunset
                <br />
                Messages
              </h1>
            </Transition>
          </div>
          <div className="flex justify-center mx-4 mt-2 md:mx-12 md:justify-start">
            <a
              href="https://www.producthunt.com/posts/sunset-messages?embed=true&utm_source=badge-featured&utm_medium=badge&utm_souce=badge-sunset&#0045;messages"
              target="_blank"
            >
              <img
                src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=485065&theme=light"
                alt="Sunset&#0032;Messages - Profound&#0032;timing&#0032;and&#0032;delivery&#0032;of&#0032;your&#0032;most&#0032;important&#0032;words | Product Hunt"
                style={{ width: '250px', height: '54px' }}
                width="250"
                height="54"
              />
            </a>
          </div>
        </header>

        <div className="flex-1 pb-12">
          <div className="bg-background-dark">
            <div className="h-8 bg-white"></div>
            <div className="mt-0">
              <div className="bg-white">
                <div className="mx-4 mt-2 prose prose-lg max-w-none md:prose-xl prose-headings:text-ssm-purple prose-p:text-ssm-purple md:mx-12 md:mt-0">
                  <h2>
                    Send messages to your loved ones…{' '}
                    <br className="hidden lg:block" />
                    after your sunset.
                  </h2>
                  <p>
                    Sunset Messages is an innovative way for you to send
                    messages to your loved ones after you pass away.
                  </p>
                  <p>
                    <b>All of your messages have the following features:</b>
                  </p>
                  <ul className="prose-ul:text-ssm-purple prose-li:text-ssm-purple prose-li:marker:text-ssm-purple">
                    <li>
                      <b>Unlimited Editing</b> – buy today, finish tomorrow,
                      update as often as you want
                    </li>
                    <li>
                      <b>Video, Audio & Photos</b> – to help you fully express
                      yourself
                    </li>
                    <li>
                      <b>Auto-save</b> – so you don’t have to think about it
                    </li>
                    <li>
                      <b>Private</b> – only you and your recipient(s) will ever
                      see the messages
                    </li>
                    <li>
                      <b>Secure</b> – our servers offer the most modern security
                      for your peace of mind
                    </li>
                  </ul>
                  <p>
                    The truth is that one day we are all going to pass from this
                    life. At that point we are no longer able to communicate
                    with those we leave behind.
                  </p>
                  <p>
                    Imagine the comfort you could bring to those you leave
                    behind with a simple Sunset Message from you.
                  </p>
                </div>
                <div className="mt-8 mb-12">
                  <div className="flex flex-wrap justify-center gap-8">
                    <Button
                      type="link"
                      to="/app/messages/add"
                      size="lg"
                      variant="primary"
                    >
                      Create a Message
                    </Button>

                    <Button
                      type="link"
                      to="/landing"
                      size="lg"
                      variant="tertiary"
                    >
                      Find Out More
                    </Button>
                  </div>
                </div>
                {/* Slide 1 */}
                <div className="flex flex-col items-center mt-8 md:flex-row">
                  <div>
                    <img
                      className="w-full object-cover object-left md:h-96 md:w-96 xl:h-[512px] xl:w-[728px]"
                      src="/images/shutterstock_89945329-resized.jpg"
                      alt="Woman"
                    />
                  </div>
                  <div className="flex-1 mx-4 mt-8 prose prose-lg md:prose-xl prose-headings:text-ssm-purple prose-p:text-ssm-purple md:mx-12 md:mt-0">
                    <h2>Leave a Legacy</h2>
                    <p>
                      These special Sunset Messages give you the ability to
                      leave a legacy well beyond your years that your loved ones
                      can cherish forever.
                    </p>
                    <p>
                      Your messages will be sent via email and text message and
                      can contain words, photos, audio and video.
                    </p>
                  </div>
                </div>
                {/* Slide 2 */}
                <div className="flex flex-col items-center mt-8 md:mt-20 md:flex-row-reverse">
                  <div>
                    <img
                      className="w-full object-cover object-center md:h-96 md:w-96 xl:h-[512px] xl:w-[768px]"
                      src="/images/xavier-mouton-photographie-ry_sD0P1ZL0-unsplash-resized.jpg"
                      alt="Mom and son at the beach"
                    />
                  </div>
                  <div className="flex-1 mx-4 mt-8 prose prose-lg md:prose-xl prose-headings:text-ssm-purple prose-p:text-ssm-purple md:mx-12 md:mt-0">
                    <h2>Joy and Comfort</h2>
                    <p>
                      Imagine the joy and comfort you could bring to your loved
                      ones even after you leave this world… after your sunset.
                    </p>
                    <ul className="prose-ul:text-ssm-purple prose-li:text-ssm-purple prose-li:marker:text-ssm-purple">
                      <li>
                        Pass on bits of wisdom, encouragement and life lessons.
                      </li>
                      <li>
                        Honor them on birthdays, anniversaries, and other
                        special occasions.
                      </li>
                      <li>
                        Leave an audio message so they can always hear your
                        voice.
                      </li>
                      <li>
                        Send photos or videos so they can revisit fond memories
                        with you.
                      </li>
                      <li>
                        Let them know it’s OK to move on with their lives.
                      </li>
                    </ul>
                  </div>
                </div>
                {/* Slide 3 */}
                <div className="flex flex-col items-center mt-8 md:mt-20 md:flex-row">
                  <div>
                    <img
                      className="w-full object-cover object-center md:h-96 md:w-96 xl:h-[512px] xl:w-[768px]"
                      src="/images/shutterstock_1710279517-resized.jpg"
                      alt="Birthday girl"
                    />
                  </div>
                  <div className="flex-1 mx-4 mt-8 prose prose-lg md:prose-xl prose-headings:text-ssm-purple prose-p:text-ssm-purple md:mx-12 md:mt-0">
                    <h2>It’s Very Easy</h2>
                    <p>It’s all very simple and we will guide you.</p>
                    <p>
                      We can even help you with ideas on who to send messages to
                      and what you might want to say in your messages.
                    </p>
                    <p>
                      Your messages can be sent one-time or on a recurring
                      basis.
                    </p>
                    <p>
                      Recurring messages can be sent at set intervals, such as
                      birthdays or anniversaries – or on specific dates that are
                      meaningful to you and your loved ones.
                    </p>
                  </div>
                </div>
                {/* Slide 4 */}
                <div className="flex flex-col items-center mt-8 md:mt-20 md:flex-row-reverse">
                  <div>
                    <img
                      className="w-full object-cover object-center md:h-96 md:w-96 xl:h-[512px] xl:w-[768px]"
                      src="/images/shutterstock_2041279904 Grandpa and Grandson Fishing-resized.jpg"
                      alt="Grandpa and Grandson Fishing"
                    />
                  </div>
                  <div className="flex-1 mx-4 mt-8 prose prose-lg md:prose-xl prose-headings:text-ssm-purple prose-p:text-ssm-purple md:mx-12 md:mt-0">
                    <h2>Message Creation and Delivery</h2>
                    <p>
                      As you create messages they will be stored in your
                      personal Message Vault on secure servers until your
                      Sunset.
                    </p>
                    <p>
                      Once we receive notice of your death through your Delivery
                      Delegate and confirm your passing, we will release the
                      messages to your intended recipients.
                    </p>
                  </div>
                </div>
                {/* Slide 5 */}
                <div className="flex flex-col items-center mt-8 md:mt-20 md:flex-row">
                  <div>
                    <img
                      className="w-full object-cover object-center md:h-96 md:w-96 xl:h-[512px] xl:w-[768px]"
                      src="/images/shutterstock_2050537937-resized.jpg"
                      alt="Messages coming out of computer keyboard"
                    />
                  </div>
                  <div className="flex-1 mx-4 mt-8 prose prose-lg md:prose-xl prose-headings:text-ssm-purple prose-p:text-ssm-purple md:mx-12 md:mt-0">
                    <h2>Ready to get started?</h2>
                    <p>Just Click below and start creating messages now.</p>
                    <p>
                      If you need some help with who to send messages to and/or
                      what to say in your messages there are suggestions on the
                      Create a Message page.
                    </p>
                    <div className="mt-8 text-center not-prose sm:text-left">
                      <Button
                        type="link"
                        to="/app/messages/add"
                        size="lg"
                        variant="primary"
                      >
                        Create a Message
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-4 py-16 mt-16 bg-purple-200">
                <Pricing />
              </div>
              <div>
                {/* Testimonials */}
                <div className="mx-4 md:mx-8 ">
                  <Testimonials />
                  <div className="mt-8">
                    <div className="flex flex-wrap justify-center gap-8">
                      <Button
                        type="link"
                        to="/app/messages/add"
                        size="lg"
                        variant="primary"
                      >
                        Create a Message
                      </Button>

                      <Button
                        type="link"
                        to="/landing"
                        size="lg"
                        variant="tertiary"
                      >
                        Find Out More
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <MainFooter />
      </div>
    </div>
  );
}
